import InputField from 'components/molecules/input-field'
import InputSelectOptions from 'components/molecules/input-select-options'
import LoaderButton from 'components/molecules/loader-button'
import SalesUsersApi from 'api/admins/sales-users-api'
import SellerOutboundReturnsApiV2 from 'api/v2/admins/seller-outbound-returns-api-v2'
import {
  CONSIGNOR_RETURN_CREATE_TYPE,
  CONSIGNOR_RETURN_SHIPMENT_TYPE,
  COURIER,
  SHIPMENT_TYPE
} from 'domains/seller-outbound-return/constants'
import InventorySelectSearchField from 'domains/seller-outbound-return/molecules/inventory-select-search-field'
import SellerSelectSearchField from 'domains/seller/molecules/select-search-field'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { Alert, Button, Col, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap'
import StringUtil from 'utils/string-util'
import sellerOutboundReturnCreateSchema from './seller-outbound-return-create-schema'

const propTypes = {
  handleSubmit: PropTypes.func.isRequired
}

const initialValues = {
  seller_id                  : '',
  user_detail_id             : '',
  return_type                : '',
  courier                    : '',
  shipment_type              : '',
  estimated_shipping_cost    : '',
  address_id                 : '',
  street_address             : '',
  complete_address           : '',
  recipient_name             : '',
  recipient_number           : '',
  instruction                : '',
  latitude                   : '',
  longitude                  : '',
  selected_inventory         : '',
  selected_inventory_quantity: 1,
  allow_unowned_items        : false,
  items                      : []
}

const getInventorySku = (inventory) => (
  inventory?.inventory_sku || inventory?.product_sku || inventory?.sku || ''
)

const getInventoryProductPhotoUrl = (inventory) => (
  inventory?.product_photo_url ||
  inventory?.product_image_url ||
  inventory?.main_image_url ||
  inventory?.photo_url ||
  inventory?.product?.photo_url ||
  inventory?.product?.main_image_url ||
  (Array.isArray(inventory?.product_photo_urls) ? inventory.product_photo_urls.find(Boolean) : null) ||
  (Array.isArray(inventory?.product_image_urls) ? inventory.product_image_urls.find(Boolean) : null) ||
  ''
)

const getInventorySellerId = (inventory) => (
  inventory?.seller_id ||
  inventory?.consignor_id ||
  inventory?.seller?.id ||
  inventory?.consignor?.id ||
  inventory?.user_id ||
  inventory?.user?.id ||
  ''
)

const getInventorySellerName = (inventory) => (
  inventory?.seller_name ||
  inventory?.consignor_seller_name ||
  inventory?.consignor_name ||
  inventory?.seller?.name ||
  inventory?.consignor?.name ||
  '-'
)

const isInventoryOwnedBySeller = (inventory, sellerId) => {
  if (!sellerId) return false
  if (inventory?.belongs_to_selected_seller === true) return true
  if (inventory?.belongs_to_selected_seller === false) return false

  return String(getInventorySellerId(inventory)) === String(sellerId)
}

const getUnownedReturnItems = (items = []) => (
  Array.isArray(items)
    ? items.filter((item) => item?.belongs_to_selected_seller === false)
    : []
)

const formatEstimatedShippingCost = (value) => {
  if (value === '' || value === null || value === undefined) return '-'

  const numericValue = Number(value)

  return StringUtil.currencyCase(numericValue === 0 ? '0' : numericValue)
}

const getEstimatedShippingCostLabel = ({ value, isLoading, isProductRequired }) => {
  if (isLoading) return 'Calculating...'

  if (value !== '' && value !== null && value !== undefined) {
    return formatEstimatedShippingCost(value)
  }

  if (isProductRequired) {
    return 'Estimasi ongkir belum tersedia karena belum ada inventory dengan IQ > 0 milik consignor.'
  }

  return '-'
}

const getShipmentTypeOption = (shipmentType) => (
  Object.values(CONSIGNOR_RETURN_SHIPMENT_TYPE).find((option) => option.value === shipmentType)
)

const getOptionCost = (shipmentCostMap = {}, shipmentType) => {
  if (!shipmentType) return undefined

  const cost = shipmentCostMap[shipmentType]

  if (cost === null || cost === undefined || cost === '') return undefined

  return cost
}

const getShipmentTypeDropdownOptions = (shipmentCostMap = {}) => Object.keys(CONSIGNOR_RETURN_SHIPMENT_TYPE).reduce((options, key) => {
  const option = CONSIGNOR_RETURN_SHIPMENT_TYPE[key]
  const estimatedShippingCost = getOptionCost(shipmentCostMap, option.value)
  const label = estimatedShippingCost === undefined
    ? option.label
    : `${option.label} - ${formatEstimatedShippingCost(estimatedShippingCost)}`

  return {
    ...options,
    [key]: {
      ...option,
      label
    }
  }
}, {})

const buildEstimatePayload = ({ sellerId, shipmentType, addressId, items = [] }) => ({
  seller_id             : sellerId,
  shipment_type         : shipmentType || '',
  address_id            : addressId || '',
  allow_unowned_items   : getUnownedReturnItems(items).length > 0,
  items                 : items.map((item) => ({
    inventory_id : item.inventory_id,
    sku          : item.sku,
    quantity     : item.quantity
  }))
})

const buildShipmentCostMap = (shipmentOptions = []) => shipmentOptions.reduce((acc, option) => ({
  ...acc,
  [option.value]: option.estimated_shipping_cost
}), {})

const getInventoryMaxQuantity = (inventory) => {
  if (!inventory) return 0

  const storedQuantity = Number(inventory.stored_quantity ?? inventory.stored_quantity_value ?? inventory.quantity ?? 0)

  return storedQuantity > 0 ? storedQuantity : 0
}

const isSelfPickup = (shipmentType) => {
  const shipmentOption = getShipmentTypeOption(shipmentType)
  const selfPickupValues = [
    SHIPMENT_TYPE.SELLER_PICKUP,
    SHIPMENT_TYPE.SELF_PICKUP,
    'seller_pickup',
    'self_pickup'
  ].filter(Boolean)

  return selfPickupValues.includes(shipmentType) || shipmentOption?.courier === COURIER.SELLER
}

const getUserAddressPageLink = (userId) => (
  userId ? `/app/sales/users/${userId}` : '/app/sales/users'
)

const getSellerIdFromOption = (seller) => (
  seller?.id || seller?.value || seller?.seller_id || seller?.target?.value || seller || ''
)

const getUserDetailIdFromOption = (seller) => (
  seller?.user_detail_id || seller?.userDetailId || seller?.detail_id || seller?.seller_detail_id || ''
)

const getDetailName = (detail) => (
  detail?.name ||
  [
    detail?.first_name || detail?.firstname,
    detail?.last_name || detail?.lastname
  ].filter(Boolean).join(' ')
).toString().trim()

const getDetailLabel = (detail) => {
  if (!detail) return '-'

  const name = getDetailName(detail)
  const phone = (detail.phone || detail.phone_number || detail.mobile_phone || '').toString().trim()

  return [ name, phone ].filter(Boolean).join(' - ') || `Detail #${detail.id}`
}

const pickPreferredDetail = (details, preferredDetailId, selectedSeller = null) => {
  const list = Array.isArray(details) ? details.filter(Boolean) : []

  if (!list.length) return null

  if (preferredDetailId) {
    const matchedById = list.find((detail) => String(detail?.id) === String(preferredDetailId))

    if (matchedById) return matchedById
  }

  const selectedLabel = (selectedSeller?.label || selectedSeller?.name || '').toString().trim().toLowerCase()

  if (selectedLabel) {
    const matchedByName = list.find((detail) => getDetailName(detail).toLowerCase() === selectedLabel)

    if (matchedByName) return matchedByName
  }

  return list.find((detail) => detail?.primary || detail?.is_primary || detail?.is_default) || list[0]
}

const pickPrimary = (addresses) => {
  const list = Array.isArray(addresses) ? addresses.filter(Boolean) : []

  if (!list.length) return null

  return list.find((address) => address?.primary || address?.is_primary || address?.is_seller_default) || list[0]
}

const getAddressLabel = (address) => {
  if (!address) return '-'

  const completeAddress = (address.complete_address || address.street_address || '').toString().trim()

  if (completeAddress) {
    return completeAddress.length > 100 ? `${completeAddress.slice(0, 100)}…` : completeAddress
  }

  const recipient = [ address.recipient_name, address.recipient_number ].filter(Boolean).join(' / ')

  return recipient ? `(${recipient}) Address #${address.id}` : `Address #${address.id}`
}

const getAddressDetailId = (address) => (
  address?.user_detail_id ||
  address?.userDetailId ||
  address?.detail_id ||
  address?.seller_detail_id ||
  address?.customer_detail_id ||
  address?.user_detail?.id ||
  address?.detail?.id ||
  ''
)

const filterAddressesByDetail = (addresses, detailId) => {
  const list = Array.isArray(addresses) ? addresses.filter(Boolean) : []

  if (!detailId) return list

  const hasAddressDetailRelation = list.some((address) => !!getAddressDetailId(address))

  if (!hasAddressDetailRelation) return list

  return list.filter((address) => String(getAddressDetailId(address)) === String(detailId))
}

const clearAddressFormikValues = (setFieldValue) => {
  setFieldValue('address_id', '')
  setFieldValue('street_address', '')
  setFieldValue('complete_address', '')
  setFieldValue('recipient_name', '')
  setFieldValue('recipient_number', '')
  setFieldValue('instruction', '')
  setFieldValue('latitude', '')
  setFieldValue('longitude', '')
}

const getAddressIncompleteMessage = (address) => {
  if (!address) return 'User ini belum punya address. Buat address dulu di halaman User.'

  const missingFields = [
    !address.complete_address ? 'Complete Address' : null,
    !address.recipient_name ? 'Recipient Name' : null,
    !address.recipient_number ? 'Recipient Number' : null,
    !address.latitude ? 'Latitude' : null,
    !address.longitude ? 'Longitude' : null
  ].filter(Boolean)

  if (!missingFields.length) return ''

  return `Alamat consignor belum lengkap. Lengkapi ${missingFields.join(', ')} terlebih dahulu agar estimasi ongkir bisa dihitung.`
}

const getAddressGuidanceMessage = ({ userMeta, isUserMetaLoading, selectedAddress, shipmentAddressWarning }) => {
  if (shipmentAddressWarning?.message) return shipmentAddressWarning.message
  if (isUserMetaLoading) return 'Loading consignor addresses…'
  if (!userMeta) return 'Pilih consignor terlebih dahulu untuk melihat daftar alamat detail.'

  const addresses = Array.isArray(userMeta?.addresses) ? userMeta.addresses : []

  if (!addresses.length) return 'User ini belum punya address. Buat address dulu di halaman User.'
  if (!selectedAddress) return 'Pilih Alamat Detail yang akan dipakai untuk pengiriman Consignor Return.'

  const incompleteMessage = getAddressIncompleteMessage(selectedAddress)

  if (incompleteMessage) return incompleteMessage

  return 'Pilih Alamat Detail yang akan dipakai untuk pengiriman Consignor Return. Estimasi ongkir akan dihitung berdasarkan alamat yang dipilih.'
}

const applyUserAddressToFormik = (address, setFieldValue) => {
  if (!address || !setFieldValue) return

  setFieldValue('address_id', address.id || '')
  setFieldValue('street_address', address.complete_address || address.street_address || '')
  setFieldValue('complete_address', address.complete_address || address.street_address || '')
  setFieldValue('recipient_name', address.recipient_name || '')
  setFieldValue('recipient_number', address.recipient_number || '')
  setFieldValue('instruction', address.instruction || '')
  setFieldValue('latitude', address.latitude || '')
  setFieldValue('longitude', address.longitude || '')
}

const SellerOutboundReturnCreateForm = ({
  handleSubmit
}) => {
  const [ itemValidationError, setItemValidationError ] = useState('')
  const [ shipmentCostMap, setShipmentCostMap ] = useState({})
  const [ shipmentEstimateError, setShipmentEstimateError ] = useState('')
  const [ shipmentAddressWarning, setShipmentAddressWarning ] = useState(null)
  const [ isShipmentEstimateLoading, setIsShipmentEstimateLoading ] = useState(false)
  const [ isShipmentProductRequired, setIsShipmentProductRequired ] = useState(false)
  const [ userMeta, setUserMeta ] = useState(null)
  const [ isUserMetaLoading, setIsUserMetaLoading ] = useState(false)
  const [ userMetaError, setUserMetaError ] = useState('')
  const [ unownedConfirmData, setUnownedConfirmData ] = useState(null)

  const handleCloseUnownedConfirm = () => {
    setUnownedConfirmData(null)
  }

  const handleConfirmUnownedSubmit = () => {
    if (!unownedConfirmData) return

    const { values, formikBag } = unownedConfirmData

    setUnownedConfirmData(null)
    formikBag.setSubmitting(true)

    Promise.resolve(handleSubmit({
      ...values,
      allow_unowned_items: true
    }, formikBag)).finally(() => {
      formikBag.setSubmitting(false)
    })
  }

  return (
    <Formik
      enableReinitialize={ false }
      initialValues={ initialValues }
      validationSchema={ sellerOutboundReturnCreateSchema }
      onSubmit={ (values, formikBag) => {
        const unownedItems = getUnownedReturnItems(values.items)

        if (unownedItems.length > 0) {
          formikBag.setSubmitting(false)
          setUnownedConfirmData({
            values,
            formikBag,
            items: unownedItems
          })
          return
        }

        return handleSubmit(values, formikBag)
      } }
    >
      {
        (formikBag) => {
          const { values, setFieldValue, setFieldError } = formikBag
          const baseError = formikBag?.errors?.base

          const syncEstimatedShippingCost = ({ sellerId, shipmentType, addressId = values.address_id, items = values.items }) => {
            if (!sellerId || !shipmentType) {
              setShipmentEstimateError('')
              setShipmentAddressWarning(null)
              setIsShipmentProductRequired(false)
              setFieldValue('estimated_shipping_cost', '')
              return
            }

            setIsShipmentEstimateLoading(true)
            setShipmentEstimateError('')
            setShipmentAddressWarning(null)
            setIsShipmentProductRequired(false)

            SellerOutboundReturnsApiV2.estimateShippingCost(buildEstimatePayload({
              sellerId,
              shipmentType,
              addressId,
              items
            }))
              .then((response) => {
                const shipmentOptions = response.data.shipment_options || []
                const nextCostMap = buildShipmentCostMap(shipmentOptions)
                const requiresAddressCompletion = response.data.requires_address_completion
                const selectedCost = response.data.estimated_shipping_cost ?? nextCostMap[shipmentType]
                const responseMessage = response.data.message || ''

                setShipmentCostMap(nextCostMap)
                setIsShipmentProductRequired(!!response.data.product_required && !isSelfPickup(shipmentType))

                if (requiresAddressCompletion) {
                  setShipmentAddressWarning({
                    message          : response.data.warning_message || responseMessage || 'Alamat consignor belum lengkap.',
                    sellerDetailPath : response.data.seller_detail_path
                  })
                  setShipmentEstimateError('')
                  setFieldValue('estimated_shipping_cost', '')
                  return
                }

                setShipmentAddressWarning(null)
                setShipmentEstimateError(selectedCost === null || selectedCost === undefined ? responseMessage : '')
                setFieldValue('estimated_shipping_cost', selectedCost === null || selectedCost === undefined ? '' : selectedCost)
              })
              .catch((error) => {
                const message = error.response?.data?.messages?.base || error.response?.data?.message || 'Unable to calculate estimated shipping cost'

                setShipmentEstimateError(message)
                setShipmentAddressWarning(null)
                setIsShipmentProductRequired(false)
                setFieldValue('estimated_shipping_cost', '')
              })
              .finally(() => setIsShipmentEstimateLoading(false))
          }


          const setAddressCompletionWarning = ({ address, sellerId = values.seller_id, shipmentType = values.shipment_type }) => {
            if (isSelfPickup(shipmentType)) return false

            const message = getAddressIncompleteMessage(address)

            if (!message) {
              setShipmentAddressWarning(null)
              return false
            }

            setShipmentAddressWarning({
              message,
              sellerDetailPath: getUserAddressPageLink(sellerId)
            })
            setFieldValue('estimated_shipping_cost', '')
            return true
          }

          const fetchAndApplyConsignorAddresses = async ({ sellerId, shipmentType = values.shipment_type, items = values.items, preferredDetailId = null, selectedSeller = null }) => {
            if (!sellerId) {
              setUserMeta(null)
              setUserMetaError('')
              return
            }

            setIsUserMetaLoading(true)
            setUserMetaError('')

            try {
              const response = await SalesUsersApi.show({ userId: sellerId })
              const fullUser = response?.data?.user || response?.data?.data || response?.data
              const addresses = Array.isArray(fullUser?.addresses) ? fullUser.addresses : []
              const details = Array.isArray(fullUser?.details) ? fullUser.details : []
              const defaultDetail = pickPreferredDetail(details, preferredDetailId, selectedSeller)
              const detailAddresses = filterAddressesByDetail(addresses, defaultDetail?.id)
              const defaultAddress = pickPrimary(detailAddresses)

              setUserMeta(fullUser || null)
              setFieldValue('user_detail_id', defaultDetail?.id || '')

              if (defaultAddress) {
                applyUserAddressToFormik(defaultAddress, setFieldValue)
              } else {
                clearAddressFormikValues(setFieldValue)
              }

              if (shipmentType && !isSelfPickup(shipmentType)) {
                const shouldBlockEstimate = setAddressCompletionWarning({
                  address      : defaultAddress,
                  sellerId     : sellerId,
                  shipmentType : shipmentType
                })

                if (!shouldBlockEstimate) {
                  syncEstimatedShippingCost({
                    sellerId,
                    shipmentType,
                    addressId: defaultAddress?.id || '',
                    items
                  })
                }
              }
            } catch (error) {
              const message = error.response?.data?.message || error.message || 'Failed to load consignor addresses'

              setUserMeta(null)
              setUserMetaError(message)
              setShipmentAddressWarning({
                message,
                sellerDetailPath: getUserAddressPageLink(sellerId)
              })
            } finally {
              setIsUserMetaLoading(false)
            }
          }

          const handleChangeConsignorDetail = (event) => {
            const detailId = event.target.value ? parseInt(event.target.value, 10) : ''
            const detailAddresses = filterAddressesByDetail(userMeta?.addresses || [], detailId)
            const defaultAddress = pickPrimary(detailAddresses)

            setFieldValue('user_detail_id', detailId)
            setShipmentCostMap({})
            setShipmentEstimateError('')
            setShipmentAddressWarning(null)
            setIsShipmentProductRequired(false)
            setFieldValue('estimated_shipping_cost', '')

            if (defaultAddress) {
              applyUserAddressToFormik(defaultAddress, setFieldValue)
            } else {
              clearAddressFormikValues(setFieldValue)
            }

            if (values.shipment_type && !isSelfPickup(values.shipment_type)) {
              const shouldBlockEstimate = setAddressCompletionWarning({
                address      : defaultAddress,
                sellerId     : values.seller_id,
                shipmentType : values.shipment_type
              })

              if (!shouldBlockEstimate) {
                syncEstimatedShippingCost({
                  sellerId     : values.seller_id,
                  shipmentType : values.shipment_type,
                  addressId    : defaultAddress?.id || '',
                  items        : values.items
                })
              }
            }
          }

          const handleChangeAddress = (event) => {
            const addressId = event.target.value ? parseInt(event.target.value, 10) : ''
            const detailAddresses = filterAddressesByDetail(userMeta?.addresses || [], values.user_detail_id)
            const selectedAddress = detailAddresses.find((address) => String(address?.id) === String(addressId))

            setFieldValue('address_id', addressId)

            if (selectedAddress) {
              applyUserAddressToFormik(selectedAddress, setFieldValue)
            }

            if (values.shipment_type && !isSelfPickup(values.shipment_type)) {
              const shouldBlockEstimate = setAddressCompletionWarning({
                address      : selectedAddress,
                sellerId     : values.seller_id,
                shipmentType : values.shipment_type
              })

              if (!shouldBlockEstimate) {
                syncEstimatedShippingCost({
                  sellerId     : values.seller_id,
                  shipmentType : values.shipment_type,
                  addressId,
                  items        : values.items
                })
              }
            }
          }

          const handleChangeShipmentType = (event) => {
            const nextShipmentType = event.target.value
            const shipmentOption = getShipmentTypeOption(nextShipmentType)
            const nextCourier = shipmentOption?.courier || (isSelfPickup(nextShipmentType) ? COURIER.SELLER : '')

            setFieldValue('shipment_type', nextShipmentType)
            setFieldValue('estimated_shipping_cost', '')
            setFieldValue('courier', nextCourier)

            if (isSelfPickup(nextShipmentType)) {
              setFieldValue('street_address', '')
            }

            const detailAddresses = filterAddressesByDetail(userMeta?.addresses || [], values.user_detail_id)
            const selectedAddress = detailAddresses.find((address) => String(address?.id) === String(values.address_id)) || null

            if (nextShipmentType && !isSelfPickup(nextShipmentType)) {
              const shouldBlockEstimate = setAddressCompletionWarning({
                address      : selectedAddress,
                sellerId     : values.seller_id,
                shipmentType : nextShipmentType
              })

              if (shouldBlockEstimate) return
            }

            syncEstimatedShippingCost({
              sellerId     : values.seller_id,
              shipmentType : nextShipmentType,
              addressId    : values.address_id,
              items        : values.items
            })
          }

          const handleChangeSeller = (seller) => {
            const nextSellerId = getSellerIdFromOption(seller)
            const preferredDetailId = getUserDetailIdFromOption(seller)

            setItemValidationError('')
            setShipmentCostMap({})
            setShipmentEstimateError('')
            setShipmentAddressWarning(null)
            setIsShipmentProductRequired(false)
            setUserMeta(null)
            setUserMetaError('')
            setFieldValue('selected_inventory', '')
            setFieldValue('selected_inventory_quantity', 1)
            setFieldValue('items', [])
            setFieldValue('estimated_shipping_cost', '')
            setFieldValue('user_detail_id', '')
            clearAddressFormikValues(setFieldValue)

            if (nextSellerId && String(nextSellerId) !== String(values.seller_id)) {
              setFieldValue('seller_id', nextSellerId)
            }

            if (nextSellerId) {
              fetchAndApplyConsignorAddresses({
                sellerId          : nextSellerId,
                shipmentType      : values.shipment_type,
                items             : [],
                preferredDetailId : preferredDetailId,
                selectedSeller    : seller
              })
            }
          }

          const appendInventoryToItems = (inventory) => {
            const selectedInventory = inventory

            if (!selectedInventory || !selectedInventory.id) return

            const inventorySku = getInventorySku(selectedInventory)
            const maxQuantity  = getInventoryMaxQuantity(selectedInventory)
            const quantity     = maxQuantity

            if (!quantity || quantity <= 0) {
              const message = `Inventory ${inventorySku} tidak eligible untuk Consignor Return karena IQ tidak tersedia.`

              setItemValidationError(message)
              setFieldError('selected_inventory', message)
              return
            }

            const isDuplicate = values.items.some((item) => (
              String(item.inventory_id) === String(selectedInventory.id) || item.sku === inventorySku
            ))

            if (isDuplicate) {
              const message = `Inventory ${inventorySku} sudah ada di list return.`

              setItemValidationError(message)
              setFieldError('selected_inventory', message)
              return
            }

            const sellableQuantity = Number(selectedInventory.sellable_quantity ?? selectedInventory.sellable_quantity_value ?? 0)
            const storedQuantity   = Number(selectedInventory.stored_quantity ?? selectedInventory.stored_quantity_value ?? selectedInventory.quantity ?? 0)
            const ownedBySeller    = isInventoryOwnedBySeller(selectedInventory, values.seller_id)

            const nextItems = [
              ...values.items,
              {
                inventory_id      : selectedInventory.id,
                sku               : inventorySku,
                product_sku       : selectedInventory.product_sku || inventorySku,
                product_name      : selectedInventory.product_name,
                product_photo_url          : getInventoryProductPhotoUrl(selectedInventory),
                max_quantity               : maxQuantity,
                quantity                   : quantity,
                sellable_quantity          : sellableQuantity,
                stored_quantity            : storedQuantity,
                seller_id                  : getInventorySellerId(selectedInventory),
                seller_name                : getInventorySellerName(selectedInventory),
                belongs_to_selected_seller : ownedBySeller
              }
            ]

            setItemValidationError('')
            setFieldValue('items', nextItems)
            setFieldValue('selected_inventory', '')
            setFieldValue('selected_inventory_quantity', 1)

            syncEstimatedShippingCost({
              sellerId     : values.seller_id,
              shipmentType : values.shipment_type,
              addressId    : values.address_id,
              items        : nextItems
            })
          }

          const handleAddItem = () => appendInventoryToItems(values.selected_inventory)

          const handleRemoveItem = (index) => {
            const nextItems = [ ...values.items ]

            nextItems.splice(index, 1)
            setItemValidationError('')
            setFieldValue('items', nextItems)

            syncEstimatedShippingCost({
              sellerId     : values.seller_id,
              shipmentType : values.shipment_type,
              addressId    : values.address_id,
              items        : nextItems
            })
          }

          const selectedInventory        = values.selected_inventory
          const selectedMaxQty           = getInventoryMaxQuantity(selectedInventory)
          const canAddSelectedItem       = !!selectedInventory && !!selectedInventory.id && selectedMaxQty > 0
          const consignorDetails         = Array.isArray(userMeta?.details) ? userMeta.details : []
          const allConsignorAddresses    = Array.isArray(userMeta?.addresses) ? userMeta.addresses : []
          const consignorAddresses       = filterAddressesByDetail(allConsignorAddresses, values.user_detail_id)
          const selectedConsignorAddress = consignorAddresses.find((address) => String(address?.id) === String(values.address_id)) || null
          const addressGuidanceMessage   = getAddressGuidanceMessage({
            userMeta,
            isUserMetaLoading,
            selectedAddress        : selectedConsignorAddress,
            shipmentAddressWarning : shipmentAddressWarning
          })

          return (
            <>
              <Form>
              <Alert color='info'>
                <strong>{ 'Inventory Phase 2 Consignor Return' }</strong>
                <div>{ 'PC membuat Consignor Return dengan memilih consignor, return type, shipment type, address, dan inventory yang akan dikembalikan.' }</div>
                <div>{ 'Return tidak harus live. Syarat utama: IQ minimal 1.' }</div>
                <div>{ 'Saat submit, sistem membuat movement request_return. Jika SQ masih ada, SQ berkurang. Jika SQ sudah 0, quantity tidak berubah sampai barang dikirim keluar.' }</div>
                <div>{ 'SKU PO / placeholder PO serta group BKA/BKV tidak ditampilkan di pencarian inventory ini.' }</div>
                <div>{ 'Default pencarian inventory menampilkan item milik consignor. Gunakan Show ALL Inventory untuk case 1 consignor memiliki banyak akun / alias.' }</div>
              </Alert>

              {
                baseError &&
                (
                  <Alert color='danger'>
                    { baseError }
                  </Alert>
                )
              }

              <SellerSelectSearchField
                name='seller_id'
                label='Consignor'
                required={ true }
                searchByUserDetail={ true }
                formText='Pencarian mencakup semua User Detail (nama/nomor HP) di dalam satu email/user, sama seperti pencarian customer di Create Deal.'
                onChange={ handleChangeSeller }
              />

              {
                values.seller_id &&
                (
                  <div className='border rounded p-3 mb-3'>
                    <Label className='font-weight-bold d-block mb-3'>
                      { 'Detail Consignor' }
                    </Label>

                    <Row>
                      <Col md='6' className='mb-3 mb-md-0'>
                        <Label className='font-weight-bold'>
                          { 'Pilih User Detail' }
                          <span className='text-danger ml-1'>{ '*' }</span>
                        </Label>
                        <Input
                          type='select'
                          value={ values.user_detail_id || '' }
                          disabled={ isUserMetaLoading || !userMeta || !consignorDetails.length }
                          onChange={ handleChangeConsignorDetail }
                        >
                          <option value='' disabled>{ '-- pilih user detail --' }</option>
                          {
                            consignorDetails.map((detail) => (
                              <option key={ detail.id } value={ detail.id }>
                                { getDetailLabel(detail) }
                              </option>
                            ))
                          }
                        </Input>
                        <FormText className='text-muted'>
                          { 'Jika 1 email memiliki lebih dari 1 User Detail, pilih nama consignor yang benar di field ini.' }
                        </FormText>
                      </Col>

                      <Col md='6'>
                        <Label className='font-weight-bold'>
                          { 'Pilih Alamat Detail' }
                          <span className='text-danger ml-1'>{ '*' }</span>
                        </Label>
                        <Input
                          type='select'
                          value={ values.address_id || '' }
                          disabled={ isUserMetaLoading || !userMeta || !consignorAddresses.length }
                          onChange={ handleChangeAddress }
                        >
                          <option value='' disabled>{ '-- pilih alamat detail --' }</option>
                          {
                            consignorAddresses.map((address) => (
                              <option key={ address.id } value={ address.id }>
                                { getAddressLabel(address) }
                              </option>
                            ))
                          }
                        </Input>
                        <FormText className='text-muted'>
                          { 'Pilih alamat detail yang akan dipakai untuk pengiriman Consignor Return. Field ini mengikuti pilihan Shipping Address pada Create Deals.' }
                        </FormText>
                      </Col>
                    </Row>

                    { isUserMetaLoading && <FormText className='text-muted d-block mt-2'>{ 'Loading User Detail dan Alamat Detail…' }</FormText> }

                    {
                      userMetaError &&
                      (
                        <Alert color='danger' className='mt-2 mb-0'>
                          { userMetaError }
                        </Alert>
                      )
                    }

                    {
                      !isUserMetaLoading && userMeta && !consignorDetails.length &&
                      (
                        <Alert color='warning' className='mt-2 mb-0'>
                          { 'User ini belum punya detail. Buat User Detail dulu di halaman User.' }
                        </Alert>
                      )
                    }

                    {
                      !isUserMetaLoading && userMeta && consignorDetails.length > 0 && !consignorAddresses.length &&
                      (
                        <Alert color='warning' className='mt-2 mb-0'>
                          { 'User Detail yang dipilih belum punya alamat detail. Tambahkan User Address dulu di halaman User.' }
                        </Alert>
                      )
                    }
                  </div>
                )
              }

              <InputField
                type='select'
                name='return_type'
                label='Return Type'
                required={ true }
              >
                <InputSelectOptions
                  options={ CONSIGNOR_RETURN_CREATE_TYPE }
                  keyPrefix='return-type'
                />
              </InputField>

              {
                values.seller_id &&
                (
                  <Alert color='warning'>
                    <div className='font-weight-bold mb-2'>
                      { 'Google Maps / Pinpoint di User Address wajib diisi dengan benar karena menjadi acuan tujuan pengiriman dan estimasi ongkir.' }
                    </div>
                    <div className='mb-2'>
                      { addressGuidanceMessage }
                    </div>
                    <a
                      href={ shipmentAddressWarning?.sellerDetailPath || getUserAddressPageLink(values.seller_id) }
                      target='_blank'
                      rel='noreferrer'
                    >
                      { 'Klik edit di sini untuk tambah / lengkapi User Address' }
                    </a>
                  </Alert>
                )
              }

              <InputField
                type='select'
                name='shipment_type'
                label='Shipment Type'
                required={ true }
                onChange={ handleChangeShipmentType }
              >
                <InputSelectOptions
                  options={ getShipmentTypeDropdownOptions(shipmentCostMap) }
                  keyPrefix='shipment-type'
                  stringUtil={ (label) => label }
                />
              </InputField>

              {
                values.shipment_type && !shipmentAddressWarning &&
                (
                  <Alert color='light' className='border'>
                    <strong>{ 'Estimasi Ongkir: ' }</strong>
                    {
                      getEstimatedShippingCostLabel({
                        value             : values.estimated_shipping_cost,
                        isLoading         : isShipmentEstimateLoading,
                        isProductRequired : isShipmentProductRequired
                      })
                    }
                  </Alert>
                )
              }

              {
                shipmentEstimateError && !shipmentAddressWarning &&
                (
                  <Alert color='warning'>
                    { shipmentEstimateError }
                  </Alert>
                )
              }

              <hr />

              <h4>{ 'Product Table' }</h4>
              <p className='text-muted mb-3'>
                { 'Setelah consignor dipilih, list inventory milik consignor dengan IQ > 0 akan langsung tampil di bawah. Klik Show ALL Inventory jika item berada di akun consignor lain / alias.' }
              </p>

              <InventorySelectSearchField
                name='selected_inventory'
                label='Cari Inventory'
                required={ false }
                sellerId={ values.seller_id }
                selectedItemIds={ values.items.map((item) => item.inventory_id) }
                showSellerInventoryList={ true }
                onAddInventory={ appendInventoryToItems }
              />

              {
                selectedInventory?.id &&
                (
                  <Alert color='light' className='border mb-3'>
                    { `Qty return otomatis: ${selectedMaxQty}` }
                  </Alert>
                )
              }

              {
                itemValidationError &&
                (
                  <Alert color='warning'>
                    { itemValidationError }
                  </Alert>
                )
              }

              <Button
                color='primary'
                type='button'
                className='mb-4'
                onClick={ handleAddItem }
                disabled={ !canAddSelectedItem }
              >
                { 'Add Item to Consignor Return' }
              </Button>

              <Table bordered responsive>
                <thead>
                  <tr>
                    <th>{ 'No.' }</th>
                    <th>{ 'Foto Product' }</th>
                    <th>{ 'SKU Product' }</th>
                    <th>{ 'Product Name' }</th>
                    <th>{ 'Consignor Owner' }</th>
                    <th>{ 'Qty' }</th>
                    <th>{ 'Action' }</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    values.items.length === 0
                      ? (
                        <tr>
                          <td colSpan='7' className='text-center text-muted'>
                            { 'Belum ada product. Tambahkan product dari pencarian inventory di atas.' }
                          </td>
                        </tr>
                      )
                      : values.items.map((item, index) => (
                        <tr key={ `${item.inventory_id}-${index}` }>
                          <td>{ index + 1 }</td>
                          <td>
                            {
                              item.product_photo_url
                                ? (
                                  <img
                                    src={ item.product_photo_url }
                                    alt={ item.product_name || item.sku }
                                    style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '6px' }}
                                  />
                                )
                                : '-'
                            }
                          </td>
                          <td>{ item.product_sku || item.sku || '-' }</td>
                          <td>{ item.product_name || '-' }</td>
                          <td>
                            <div>{ item.seller_name || '-' }</div>
                            {
                              item.belongs_to_selected_seller === false &&
                              <small className='text-danger font-weight-bold'>{ 'Bukan milik consignor terpilih' }</small>
                            }
                          </td>
                          <td>{ item.quantity }</td>
                          <td>
                            <Button
                              color='danger'
                              size='sm'
                              type='button'
                              onClick={ () => handleRemoveItem(index) }
                            >
                              { 'Remove' }
                            </Button>
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
              </Table>

              <LoaderButton
                color='primary'
                type='submit'
                onSubmit={ formikBag.submitForm }
                disabled={ formikBag.isSubmitting || isShipmentEstimateLoading || (!!shipmentAddressWarning && !isSelfPickup(values.shipment_type)) }
                isLoading={ formikBag.isSubmitting }
                text='Create Consignor Return'
              />
              </Form>

              <Modal isOpen={ !!unownedConfirmData } toggle={ handleCloseUnownedConfirm } size='lg'>
                <ModalHeader toggle={ handleCloseUnownedConfirm }>{ 'Confirm Different Consignor Inventory' }</ModalHeader>
                <ModalBody>
                  <Alert color='warning'>
                    { 'Ada product yang bukan milik consignor terpilih. Lanjutkan hanya jika ini memang case consignor punya banyak akun / alias dan product harus dikembalikan ke consignor yang sedang dipilih.' }
                  </Alert>

                  <Table bordered responsive size='sm' className='mb-0'>
                    <thead>
                      <tr>
                        <th>{ 'SKU' }</th>
                        <th>{ 'Product Name' }</th>
                        <th>{ 'Actual Owner' }</th>
                        <th>{ 'Qty' }</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        (unownedConfirmData?.items || []).map((item) => (
                          <tr key={ item.inventory_id || item.sku }>
                            <td>{ item.product_sku || item.sku || '-' }</td>
                            <td>{ item.product_name || '-' }</td>
                            <td>{ item.seller_name || '-' }</td>
                            <td>{ item.quantity }</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </Table>
                </ModalBody>
                <ModalFooter>
                  <Button color='secondary' type='button' onClick={ handleCloseUnownedConfirm }>
                    { 'Cancel' }
                  </Button>
                  <Button color='warning' type='button' onClick={ handleConfirmUnownedSubmit }>
                    { 'Confirm & Create Consignor Return' }
                  </Button>
                </ModalFooter>
              </Modal>
            </>
          )
        }
      }
    </Formik>
  )
}

SellerOutboundReturnCreateForm.propTypes = propTypes

export default SellerOutboundReturnCreateForm
